/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable import/prefer-default-export */
/**
 * Determines the granularity based on the difference in calendar days between
 * the start and end dates of a target date range.
 * @param {Object} targetDate - The target date range object with 'startDate' and 'endDate'.
 * @param {Date} targetDate.startDate - The start date of the target range.
 * @param {Date} targetDate.endDate - The end date of the target range.
 * @returns {string|undefined} - The calculated granularity, or undefined if not matched.
 */

import {
  differenceInCalendarDays,
  endOfMonth,
  endOfYear,
  format,
  isAfter,
  isBefore,
  isSaturday,
  isSunday,
  max,
  nextSaturday,
  previousSunday,
  startOfDay,
  startOfMonth,
  startOfYear,
  subDays,
} from 'date-fns';
import { PACE_HORIZON_SELECTION } from 'modules/dashboard/constants';
import calculateComparisonDates from 'modules/common/utils/dateComparisonUtils';
import { compareWindows } from 'modules/common/ui-controller';
import { YEAR_MONTH_DAY_PATTERN } from 'modules/common/constants/date-range';
import {
  BOOKING_DATE_COMPARISON,
  BOOKING_DATE_RANGE,
  COMPARE_WINDOW_MAP,
  COMPARISON,
  CERULEAN_GRANULARITY_THRESHOLD,
  GRANULARITY,
  MAX_BOOKING_FILTER,
  PACE_HORIZON,
  PACE_HORIZON_FILTER,
  PACE_HOTEL_CUMULATIVE_COLUMNS,
  PACE_HOTEL_CUMULATIVE_FORECAST_COLUMNS,
  PACE_HOTEL_CUMULATIVE_LAG_ALIGNED_COLUMNS,
  PACE_HOTEL_CUMULATIVE_LAG_ALIGNED_FORECAST_COLUMNS,
  PACE_HOTEL_CUMULATIVE_LAG_ALIGNED_SCHEMA,
  PACE_HOTEL_DAILY_COLUMNS,
  PACE_HOTEL_DAILY_FORECAST_COLUMNS,
  PACE_HOTEL_DAILY_FORECAST_SCHEMA,
  PACE_HOTEL_DAILY_SCHEMA,
  PACE_HOTEL_LAG_ALIGNED_COLUMNS,
  PACE_HOTEL_LAG_ALIGNED_FORECAST_COLUMNS,
  PACE_HOTEL_LAG_ALIGNED_SCHEMA,
  PACE_SEGMENT_CUMULATIVE_COLUMNS,
  PACE_SEGMENT_CUMULATIVE_LAG_ALIGNED_COLUMNS,
  PACE_SEGMENT_DAILY_COLUMNS,
  PACE_SEGMENT_LAG_ALIGNED_COLUMNS,
  PACE_HOTEL_SEGMENT_CUMULATIVE_COLUMNS,
  PACE_HOTEL_SEGMENT_CUMULATIVE_SCHEMA,
  PACE_HOTEL_SEGMENT_CUMULATIVE_TITLE,
  PACE_HOTEL_SEGMENT_DAILY_COLUMNS,
  PACE_HOTEL_SEGMENT_DAILY_SCHEMA,
  PACE_HOTEL_SEGMENT_CUMULATIVE_LAG_ALIGNED_SCHEMA,
  PACE_HOTEL_SEGMENT_CUMULATIVE_LAG_ALIGNED_TITLE,
  PACE_HOTEL_SEGMENT_LAG_ALIGNED_COLUMNS,
  PACE_HOTEL_SEGMENT_LAG_ALIGNED_CUMULATIVE_COLUMNS,
  PACE_HOTEL_SEGMENT_LAG_ALIGNED_SCHEMA,
  PACE_HOTEL_SEGMENT_LAG_ALIGNED_TITLE,
  PACE_HOTEL_SEGMENT_TABULAR_TITLE,
  PACE_TIME_SEGMENT_CUMULATIVE_COLUMNS,
  PACE_TIME_SEGMENT_CUMULATIVE_LAG_ALIGNED_SCHEMA,
  PACE_TIME_SEGMENT_CUMULATIVE_LAG_ALIGNED_TITLE,
  PACE_TIME_SEGMENT_CUMULATIVE_SCHEMA,
  PACE_TIME_SEGMENT_CUMULATIVE_TITLE,
  PACE_TIME_SEGMENT_LAG_ALIGNED_COLUMNS,
  PACE_TIME_SEGMENT_LAG_ALIGNED_CUMULATIVE_COLUMNS,
  PACE_TIME_SEGMENT_LAG_ALIGNED_SCHEMA,
  PACE_TIME_SEGMENT_LAG_ALIGNED_TITLE,
  PACE_TIME_COLUMNS,
  PACE_TIME_CUMULATIVE_COLUMNS,
  PACE_TIME_CUMULATIVE_FORECAST_SCHEMA,
  PACE_TIME_CUMULATIVE_LAG_ALIGNED__TITLE,
  PACE_TIME_CUMULATIVE_SCHEMA,
  PACE_TIME_CUMULATIVE_TITLE,
  PACE_TIME_FORECAST_COLUMNS,
  PACE_TIME_FORECAST_CUMULATIVE_COLUMNS,
  PACE_TIME_FORECAST_CUMULATIVE_LAG_ALIGNED_COLUMNS,
  PACE_TIME_FORECAST_LAG_ALIGNED_COLUMNS,
  PACE_TIME_FORECAST_SCHEMA,
  PACE_TIME_LAG_ALIGNED_COLUMNS,
  PACE_TIME_LAG_ALIGNED_CUMULATIVE_COLUMNS,
  PACE_TIME_LAG_ALIGNED_CUMULATIVE_FORECAST_SCHEMA,
  PACE_TIME_LAG_ALIGNED_CUMULATIVE_SCHEMA,
  PACE_TIME_LAG_ALIGNED_FORECAST_SCHEMA,
  PACE_TIME_LAG_ALIGNED_SCHEMA,
  PACE_TIME_SCHEMA,
  PACE_TIME_TABULAR_LAG_ALIGNED_TITLE,
  PACE_TIME_TABULAR_TITLE,
  REPORTING_CURRENCY,
  STAY_DATE_COMPARISON,
  STAY_DATE_RANGE,
  PACE_SEGMENT_CUMULATIVE_LAG_ALIGNED_TITLE,
  PACE_SEGMENT_CUMULATIVE_TITLE,
  PACE_SEGMENT_LAG_ALIGNED_TITLE,
  PACE_SEGMENT_TABULAR_TITLE,
  PACE_TIME_SEGMENT_TITLE,
  PACE_TIME_SEGMENT_COLUMNS,
  PACE_TIME_SEGMENT_SCHEMA,
  PACE_HOTEL_LAG_ALIGNED_CUMULATIVE_FORECAST_SCHEMA,
  PACE_HOTEL_LAG_ALIGNED_FORECAST_SCHEMA,
  PACE_HOTEL_CUMULATIVE_FORECAST_SCHEMA,
} from './constants';
import { getSubPaceFilter } from '../inner-filter/functions';
import {
  createCeruleanBreakdownSchemaFilter,
  createCeruleanDateFilter,
  createCeruleanFilter,
  getColumnsBySuffix,
} from '../trend-tabs/functions';
import {
  ALIGNMENT_FILTER,
  BOOKING_DATE_LOWER,
  BOOKING_DATE_UPPER,
  BREAKDOWN_LIST_FILTER,
  CERULEAN_TREND_GRANULARITY,
  COMPARE_BOOKING_DATE_LOWER,
  COMPARE_BOOKING_DATE_UPPER,
  COMPARE_STAY_DATE_LOWER,
  COMPARE_STAY_DATE_UPPER,
  COMPARISON_WINDOW_FILTER,
  CURRENCY_FILTER,
  GRANULARITY_FILTER,
  HOTEL_COMP_FILTER,
  HOTEL_FILTER,
  NORMALIZED_FILTER,
  PMS_FILTER,
  STAY_DATE_LOWER,
  STAY_DATE_UPPER,
} from '../trend-tabs/constants';

/**
 * Returns the numeric day count parsed from the label of the selected pace
 * horizon filter option (e.g. "30 Days" -> "30").
 * @param {Object} visualFilters - Applied visual-level filters.
 * @param {string|number} paceHorizon - Selected pace horizon identifier.
 * @returns {string|undefined} Day count as a string, or undefined if not found.
 */
const getPaceHorizonDays = (visualFilters, paceHorizon) =>
  getSubPaceFilter(visualFilters)
    ?.find((filter) => filter?.id === paceHorizon)
    ?.label[0].split(' ')[0];

export const getGranularity = (bookingDate, paceHorizon, bookingPaceWindow, visualFilters) => {
  let granularity;
  let difference;

  if (paceHorizon && visualFilters && bookingDate) {
    if (bookingPaceWindow === PACE_HORIZON_SELECTION.BOOKING_PACE_WINDOW) {
      difference =
        differenceInCalendarDays(new Date(bookingDate.endDate), new Date(bookingDate.startDate)) +
        1;
    } else {
      difference = Number(getPaceHorizonDays(visualFilters, paceHorizon));
    }
  }
  //
  if (difference <= 60) {
    granularity = GRANULARITY.DAILY;
  } else if (difference < 365) {
    granularity = GRANULARITY.WEEKLY;
  } else if (difference <= 1460) {
    granularity = GRANULARITY.MONTHLY;
  } else {
    granularity = GRANULARITY.YEARLY;
  }
  return granularity;
};
/**
 * Resolves the custom booking window range against the maximum available confirmation date
 * (MACD) for the no forecast cases.
 * - Selected range ends on or before MACD: the selected range is used.
 * - Selected range starts on or before MACD and ends after it: the range ends on MACD.
 * - Selected range starts after MACD: the selected range is used for the header and granularity,
 *   and the visuals show no data (see isCustomBookingAfterMaxConfirmationDate).
 * Forecast cases are not defined yet, so the selected range is used as is when forecast is on.
 * @param {{startDate: string|Date, endDate: string|Date}} paceBookingDate - Selected booking range.
 * @param {string|Date} lastBookingDate - Maximum available confirmation date (MACD).
 * @param {boolean} isForecast - Indicates whether forecast mode is enabled.
 * @returns {{startDate: (string|Date), endDate: (string|Date)}} Resolved booking range.
 */
export const resolveCustomBookingDate = (paceBookingDate, lastBookingDate, isForecast) => {
  const selectedRange = {
    startDate: paceBookingDate?.startDate,
    endDate: paceBookingDate?.endDate,
  };
  const minimumSelectedBookingDate = startOfDay(new Date(paceBookingDate?.startDate));
  const maximumSelectedBookingDate = startOfDay(new Date(paceBookingDate?.endDate));
  const maximumConfirmationDate = startOfDay(new Date(lastBookingDate));
  if (
    isForecast ||
    !paceBookingDate?.startDate ||
    !paceBookingDate?.endDate ||
    !lastBookingDate ||
    Number.isNaN(minimumSelectedBookingDate.getTime()) ||
    Number.isNaN(maximumSelectedBookingDate.getTime()) ||
    Number.isNaN(maximumConfirmationDate.getTime())
  ) {
    return selectedRange;
  }
  if (
    !isAfter(minimumSelectedBookingDate, maximumConfirmationDate) &&
    isAfter(maximumSelectedBookingDate, maximumConfirmationDate)
  ) {
    return { ...selectedRange, endDate: maximumConfirmationDate };
  }
  return selectedRange;
};
/**
 * Checks whether the custom booking window is entirely after the maximum available confirmation
 * date (MACD) for the no forecast cases. There is no data to show for such a selection.
 * @param {string} paceBookingWindow - Selected booking window type.
 * @param {{startDate: string|Date, endDate: string|Date}} paceBookingDate - Selected booking range.
 * @param {string|Date} lastBookingDate - Maximum available confirmation date (MACD).
 * @param {boolean} isForecast - Indicates whether forecast mode is enabled.
 * @returns {boolean} True when both selected dates are after MACD.
 */
export const isCustomBookingAfterMaxConfirmationDate = (
  paceBookingWindow,
  paceBookingDate,
  lastBookingDate,
  isForecast
) => {
  if (
    paceBookingWindow !== PACE_HORIZON_SELECTION.BOOKING_PACE_WINDOW ||
    isForecast ||
    !paceBookingDate?.startDate ||
    !paceBookingDate?.endDate ||
    !lastBookingDate
  ) {
    return false;
  }
  // an invalid date is never after another date, so invalid inputs resolve to false
  const maximumConfirmationDate = startOfDay(new Date(lastBookingDate));
  return (
    isAfter(startOfDay(new Date(paceBookingDate.startDate)), maximumConfirmationDate) &&
    isAfter(startOfDay(new Date(paceBookingDate.endDate)), maximumConfirmationDate)
  );
};
/**
 * Returns the Cerulean granularity of the Pace tabular visuals. For the custom booking window the
 * day count is taken from the booking range resolved against the maximum confirmation date.
 * @param {{startDate: string|Date, endDate: string|Date}} paceBookingDate - Selected booking range.
 * @param {string|number} paceHorizon - Selected pace horizon identifier.
 * @param {string} paceBookingWindow - Selected booking window type.
 * @param {Object} visualFilters - Applied visual-level filters.
 * @param {string|Date} lastBookingDate - Maximum available confirmation date (MACD).
 * @param {boolean} isForecast - Indicates whether forecast mode is enabled.
 * @returns {string|undefined} Cerulean granularity, or undefined for missing inputs.
 */
export const getPaceTabularGranularity = (
  paceBookingDate,
  paceHorizon,
  paceBookingWindow,
  visualFilters,
  lastBookingDate,
  isForecast
) => {
  let days;
  if (paceBookingWindow === PACE_HORIZON_SELECTION.BOOKING_PACE_WINDOW) {
    if (!paceBookingDate?.startDate || !paceBookingDate?.endDate) return undefined;
    const bookingDate = resolveCustomBookingDate(paceBookingDate, lastBookingDate, isForecast);
    days =
      differenceInCalendarDays(new Date(bookingDate.endDate), new Date(bookingDate.startDate)) + 1;
  } else {
    days = Number(getPaceHorizonDays(visualFilters, paceHorizon));
  }
  if (!Number.isFinite(days) || days <= 0) return undefined;
  if (days <= CERULEAN_GRANULARITY_THRESHOLD.WEEKLY) {
    return CERULEAN_TREND_GRANULARITY.DAILY;
  }
  if (days < CERULEAN_GRANULARITY_THRESHOLD.MONTHLY) {
    return CERULEAN_TREND_GRANULARITY.WEEKLY;
  }
  if (days <= CERULEAN_GRANULARITY_THRESHOLD.YEARLY) {
    return CERULEAN_TREND_GRANULARITY.MONTHLY;
  }
  return CERULEAN_TREND_GRANULARITY.YEARLY;
};
/**
 * Snaps a date range to the boundaries of the selected granularity so the
 * final booking and comparison booking ranges always cover whole buckets.
 * Weekly ranges run Sunday through Saturday, monthly ranges cover whole
 * calendar months, and yearly ranges cover whole calendar years. Daily
 * ranges are returned unchanged.
 *
 * For detailed logic and examples, refer to the
 * [Date Adjustment Based on Granularity](https://surgeglobal.atlassian.net/wiki/x/AoDbY)
 * documentation.
 *
 * @param {{startDate: string|Date, endDate: string|Date}} dateRange - Range to adjust.
 * @param {string} granularity - Selected granularity value.
 * @returns {{startDate: Date, endDate: Date}} Adjusted date range.
 */
const adjustDateRangeByGranularity = (dateRange, granularity) => {
  const startDate = new Date(dateRange?.startDate);
  const endDate = new Date(dateRange?.endDate);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return { startDate, endDate };
  }
  switch (granularity) {
    case CERULEAN_TREND_GRANULARITY.WEEKLY:
    case GRANULARITY.WEEKLY:
      return {
        startDate: isSunday(startDate) ? startDate : previousSunday(startDate),
        endDate: isSaturday(endDate) ? endDate : nextSaturday(endDate),
      };
    case CERULEAN_TREND_GRANULARITY.MONTHLY:
    case GRANULARITY.MONTHLY:
      return {
        startDate: startOfMonth(startDate),
        endDate: endOfMonth(endDate),
      };
    case CERULEAN_TREND_GRANULARITY.YEARLY:
    case GRANULARITY.YEARLY:
      return {
        startDate: startOfYear(startDate),
        endDate: endOfYear(endDate),
      };
    default:
      return { startDate, endDate };
  }
};
/**
 * Resolves the stay comparison range, the granularity adjusted booking range and the comparison
 * booking range of the Pace tabular visuals. When symmetric comparison is on, the comparison booking
 * range is the granularity adjusted booking range shifted by the selected comparison type, and it is
 * not adjusted by the granularity again. Otherwise the comparison booking range is the reset booking
 * range adjusted by the granularity.
 * @param {Object} params
 * @param {{startDate: string, endDate: string}} params.targetDate - Selected stay date range.
 * @param {{startDate: string|Date, endDate: string|Date}} params.bookingDate - Resolved booking range.
 * @param {string} params.granularity - Selected granularity level.
 * @param {boolean} params.alignmentToggle - Indicates whether alignment mode is enabled.
 * @param {boolean} params.symmetricComparisonToggle - Indicates whether symmetric comparison is enabled.
 * @param {string} params.dateComparison - Selected comparison period.
 * @param {{startDate: string, endDate: string}} params.resetBookingDate - Reset (OTB) booking range.
 * @param {number} params.partialHours - Partial hours value used for comparison calculations.
 * @param {string|Date} params.lastBookingDate - Last available booking date.
 * @returns {{adjustedStayDate: Object, finalBookingDate: Object, finalCompareBookingDate: Object}}
 */
const resolvePaceComparisonDates = ({
  targetDate,
  bookingDate,
  granularity,
  alignmentToggle,
  symmetricComparisonToggle,
  dateComparison,
  resetBookingDate,
  partialHours,
  lastBookingDate,
}) => {
  const finalBookingDate = adjustDateRangeByGranularity(bookingDate, granularity);
  const { adjustedStayDate, adjustedBookingDate } = calculateComparisonDates(
    targetDate,
    finalBookingDate,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    null,
    resetBookingDate,
    partialHours,
    lastBookingDate,
    { strictMonthEnd: true }
  );
  const isShiftedBookingDate = symmetricComparisonToggle && dateComparison !== compareWindows.otb;
  const finalCompareBookingDate = isShiftedBookingDate
    ? adjustedBookingDate
    : adjustDateRangeByGranularity(adjustedBookingDate, granularity);
  //
  return { adjustedStayDate, finalBookingDate, finalCompareBookingDate };
};
//
const PACE_HORIZON_COMPARISON_LABEL = {
  [compareWindows.py]: 'STLY',
  [compareWindows.pm]: 'STLM',
  [compareWindows.pw]: 'STLW',
};
/**
 * Returns the Booking Date Selection header value of the Booking Pace Horizon options.
 * @param {string|Date} lastBookingDate - Maximum available confirmation date (MACD).
 * @returns {string} Header value in the form of 'OTB (to MACD)'.
 */
const getPaceHorizonBookingLabel = (lastBookingDate) =>
  `OTB (to ${format(new Date(lastBookingDate), YEAR_MONTH_DAY_PATTERN)})`;
/**
 * Returns the booking Comparison header value of the Booking Pace Horizon options. The comparison
 * date is resolved from MACD without a granularity, as the granularity adjusted end date of the
 * visual can be after MACD.
 * @param {Object} params - Same params as resolvePaceComparisonDates.
 * @returns {string|null} Header value in the form of 'STLY (to comparison MACD)', or null when the
 * selected comparison period has no label.
 */
const getPaceHorizonComparisonLabel = ({ bookingDate, ...params }) => {
  const comparisonLabel = PACE_HORIZON_COMPARISON_LABEL[params.dateComparison];
  if (!comparisonLabel) return null;
  const { finalCompareBookingDate } = resolvePaceComparisonDates({
    ...params,
    bookingDate: { startDate: bookingDate.startDate, endDate: params.lastBookingDate },
  });
  return `${comparisonLabel} (to ${format(
    new Date(finalCompareBookingDate.endDate),
    YEAR_MONTH_DAY_PATTERN
  )})`;
};
//
export const getPaceTimeColumns = (isCumulative, isForecast, isLagAligned) => {
  if (isForecast && isCumulative && isLagAligned)
    return PACE_TIME_FORECAST_CUMULATIVE_LAG_ALIGNED_COLUMNS;
  if (isForecast && isCumulative) return PACE_TIME_FORECAST_CUMULATIVE_COLUMNS;
  if (isForecast && isLagAligned) return PACE_TIME_FORECAST_LAG_ALIGNED_COLUMNS;
  if (isLagAligned && isCumulative) return PACE_TIME_LAG_ALIGNED_CUMULATIVE_COLUMNS;
  if (isForecast) return PACE_TIME_FORECAST_COLUMNS;
  if (isLagAligned) return PACE_TIME_LAG_ALIGNED_COLUMNS;
  if (isCumulative) return PACE_TIME_CUMULATIVE_COLUMNS;
  return PACE_TIME_COLUMNS;
};
export const getPaceHotelColumns = (isCumulative, isForecast, isLagAligned) => {
  if (isForecast && isCumulative && isLagAligned)
    return PACE_HOTEL_CUMULATIVE_LAG_ALIGNED_FORECAST_COLUMNS;
  if (isForecast && isCumulative) return PACE_HOTEL_CUMULATIVE_FORECAST_COLUMNS;
  if (isForecast && isLagAligned) return PACE_HOTEL_LAG_ALIGNED_FORECAST_COLUMNS;
  if (isLagAligned && isCumulative) return PACE_HOTEL_CUMULATIVE_LAG_ALIGNED_COLUMNS;
  if (isForecast) return PACE_HOTEL_DAILY_FORECAST_COLUMNS;
  if (isLagAligned) return PACE_HOTEL_LAG_ALIGNED_COLUMNS;
  if (isCumulative) return PACE_HOTEL_CUMULATIVE_COLUMNS;
  return PACE_HOTEL_DAILY_COLUMNS;
};
export const getPaceSegmentColumns = (isCumulative, isLagAligned) => {
  if (isLagAligned && isCumulative) return PACE_SEGMENT_CUMULATIVE_LAG_ALIGNED_COLUMNS;
  if (isLagAligned) return PACE_SEGMENT_LAG_ALIGNED_COLUMNS;
  if (isCumulative) return PACE_SEGMENT_CUMULATIVE_COLUMNS;
  return PACE_SEGMENT_DAILY_COLUMNS;
};
export const getPaceTimeSegmentColumns = (isCumulative, isLagAligned) => {
  if (isLagAligned && isCumulative) return PACE_TIME_SEGMENT_LAG_ALIGNED_CUMULATIVE_COLUMNS;
  if (isLagAligned) return PACE_TIME_SEGMENT_LAG_ALIGNED_COLUMNS;
  if (isCumulative) return PACE_TIME_SEGMENT_CUMULATIVE_COLUMNS;
  return PACE_TIME_SEGMENT_COLUMNS;
};
export const getPaceTimeSegmentSchema = (isCumulative, isLagAligned) => {
  if (isCumulative && isLagAligned) return PACE_TIME_SEGMENT_CUMULATIVE_LAG_ALIGNED_SCHEMA;
  if (isLagAligned) return PACE_TIME_SEGMENT_LAG_ALIGNED_SCHEMA;
  if (isCumulative) return PACE_TIME_SEGMENT_CUMULATIVE_SCHEMA;
  return PACE_TIME_SEGMENT_SCHEMA;
};
// Every combination is served by a JS table
export const getPaceHotelSegmentColumns = (isCumulative, isLagAligned) => {
  if (isCumulative && isLagAligned) return PACE_HOTEL_SEGMENT_LAG_ALIGNED_CUMULATIVE_COLUMNS;
  if (isLagAligned) return PACE_HOTEL_SEGMENT_LAG_ALIGNED_COLUMNS;
  if (isCumulative) return PACE_HOTEL_SEGMENT_CUMULATIVE_COLUMNS;
  return PACE_HOTEL_SEGMENT_DAILY_COLUMNS;
};
export const getPaceHotelSegmentSchema = (isCumulative, isLagAligned) => {
  if (isCumulative && isLagAligned) return PACE_HOTEL_SEGMENT_CUMULATIVE_LAG_ALIGNED_SCHEMA;
  if (isLagAligned) return PACE_HOTEL_SEGMENT_LAG_ALIGNED_SCHEMA;
  if (isCumulative) return PACE_HOTEL_SEGMENT_CUMULATIVE_SCHEMA;
  return PACE_HOTEL_SEGMENT_DAILY_SCHEMA;
};
export const getPaceHotelSegmentTitle = (isCumulative, isLagAligned) => {
  if (isCumulative && isLagAligned) return PACE_HOTEL_SEGMENT_CUMULATIVE_LAG_ALIGNED_TITLE;
  if (isLagAligned) return PACE_HOTEL_SEGMENT_LAG_ALIGNED_TITLE;
  if (isCumulative) return PACE_HOTEL_SEGMENT_CUMULATIVE_TITLE;
  return PACE_HOTEL_SEGMENT_TABULAR_TITLE;
};
export const getPaceHotelSegmentFileName = (isCumulative, isLagAligned) => {
  if (isCumulative && isLagAligned) return 'PHS_Cumulative_Lag_Aligned';
  if (isLagAligned) return 'PHS_Lag_Aligned';
  if (isCumulative) return 'PHS_Cumulative';
  return 'PHS_Daily';
};
export const getPaceTimeSegmentFileName = (isCumulative, isLagAligned) => {
  if (isCumulative && isLagAligned) return 'PTS_Cumulative_Lag_Aligned';
  if (isCumulative) return 'PTS_Cumulative';
  if (isLagAligned) return 'PTS_Lag_Aligned';
  return 'PTS_Daily';
};
export const getPaceTimeSegmentTitle = (isCumulative, isLagAligned) => {
  if (isCumulative && isLagAligned) return PACE_TIME_SEGMENT_CUMULATIVE_LAG_ALIGNED_TITLE;
  if (isCumulative) return PACE_TIME_SEGMENT_CUMULATIVE_TITLE;
  if (isLagAligned) return PACE_TIME_SEGMENT_LAG_ALIGNED_TITLE;
  return PACE_TIME_SEGMENT_TITLE;
};
export const getPaceTimeSchema = (isCumulative, isForecast, isLagAligned) => {
  if (isForecast && isCumulative && isLagAligned)
    return PACE_TIME_LAG_ALIGNED_CUMULATIVE_FORECAST_SCHEMA;
  if (isForecast && isCumulative) return PACE_TIME_CUMULATIVE_FORECAST_SCHEMA;
  if (isForecast && isLagAligned) return PACE_TIME_LAG_ALIGNED_FORECAST_SCHEMA;
  if (isLagAligned && isCumulative) return PACE_TIME_LAG_ALIGNED_CUMULATIVE_SCHEMA;
  if (isForecast) return PACE_TIME_FORECAST_SCHEMA;
  if (isLagAligned) return PACE_TIME_LAG_ALIGNED_SCHEMA;
  if (isCumulative) return PACE_TIME_CUMULATIVE_SCHEMA;
  return PACE_TIME_SCHEMA;
};
export const getPaceHotelSchema = (isCumulative, isLagAligned, isForecast) => {
  if (isForecast && isCumulative && isLagAligned)
    return PACE_HOTEL_LAG_ALIGNED_CUMULATIVE_FORECAST_SCHEMA;
  if (isForecast && isCumulative) return PACE_HOTEL_CUMULATIVE_FORECAST_SCHEMA; // No separate forecast schema for cumulative daily
  if (isLagAligned && isCumulative) return PACE_HOTEL_CUMULATIVE_LAG_ALIGNED_SCHEMA;
  if (isForecast && isLagAligned) return PACE_HOTEL_LAG_ALIGNED_FORECAST_SCHEMA;
  if (isLagAligned) return PACE_HOTEL_LAG_ALIGNED_SCHEMA;
  if (isForecast) return PACE_HOTEL_DAILY_FORECAST_SCHEMA;
  if (isCumulative) return PACE_HOTEL_DAILY_SCHEMA; // No separate schema for cumulative daily
  return PACE_HOTEL_DAILY_SCHEMA;
};
/**
 * Calculates the booking/confirmation date range used for Pace calculations
 * based on the selected stay dates, pace horizon, forecast settings, and
 * available booking data.
 *
 * @param {{startDate: string, endDate: string}} stayDate - Selected stay date range.
 * @param {Object} visualFilters - Applied visual-level filters containing pace horizon options.
 * @param {string|number} selectedPaceHorizon - Selected pace horizon identifier.
 * @param {string|Date} lastPartialBookingDate - Most recent available booking date.
 * @param {boolean} isForecast - Indicates whether forecast mode is enabled.
 * @param {boolean|number|string} pms - PMS state/value used to determine the forecast source.
 * @param {{
 *   roomsPmsOn: string|Date,
 *   roomsPmsOff: string|Date,
 *   revenuePmsOn: string|Date,
 *   revenuePmsOff: string|Date
 * }} maxForecastDate - Maximum available forecast dates for PMS and non-PMS data.
 *
 * @returns {{
 *   startDate: Date,
 *   endDate: Date
 * }} Calculated confirmation/booking date range used for Pace analysis.
 */
export const calculateConformationDate = (
  stayDate,
  visualFilters,
  selectedPaceHorizon,
  lastPartialBookingDate,
  isForecast,
  pms,
  maxForecastDate
) => {
  const paceHorizonDays = Math.max(
    0,
    Number(getPaceHorizonDays(visualFilters, selectedPaceHorizon)) - 1
  );
  const partialBookingDate = startOfDay(new Date(lastPartialBookingDate));
  const minimumSelectedStayDate = startOfDay(new Date(stayDate.startDate));
  const maximumSelectedStayDate = startOfDay(new Date(stayDate.endDate));
  //
  if (isForecast) {
    const maxForecast = max([
      new Date(pms ? maxForecastDate.roomsPmsOn : maxForecastDate.revenuePmsOff),
      new Date(pms ? maxForecastDate.revenuePmsOn : maxForecastDate.revenuePmsOff),
    ]);
    //
    if (isBefore(maximumSelectedStayDate, partialBookingDate)) {
      return {
        startDate: subDays(maximumSelectedStayDate, paceHorizonDays),
        endDate: maximumSelectedStayDate,
      };
    }
    if (
      isAfter(minimumSelectedStayDate, maxForecast) &&
      isAfter(maximumSelectedStayDate, maxForecast)
    ) {
      return {
        startDate: subDays(partialBookingDate, paceHorizonDays),
        endDate: partialBookingDate,
      };
    }
    if (
      isBefore(minimumSelectedStayDate, maxForecast) &&
      isAfter(maximumSelectedStayDate, maxForecast)
    ) {
      return {
        startDate: subDays(maxForecast, paceHorizonDays),
        endDate: maxForecast,
      };
    }
    if (
      isBefore(maximumSelectedStayDate, maxForecast) &&
      isAfter(maximumSelectedStayDate, partialBookingDate)
    ) {
      return {
        startDate: subDays(maximumSelectedStayDate, paceHorizonDays),
        endDate: maximumSelectedStayDate,
      };
    }
  }
  // For non-forecast scenarios, calculate the confirmation date based on the selected stay date and partial booking date.
  if (isBefore(maximumSelectedStayDate, partialBookingDate)) {
    return {
      startDate: subDays(maximumSelectedStayDate, paceHorizonDays),
      endDate: maximumSelectedStayDate,
    };
  }
  // If the selected stay date range includes or is after the partial booking date, return a confirmation date range that ends with the partial booking date and starts based on the pace horizon.
  return {
    startDate: subDays(partialBookingDate, paceHorizonDays),
    endDate: partialBookingDate,
  };
};
/**
 * Resolves the pace horizon display value and the booking date range used for
 * Pace calculations. When the booking pace window ('BPW') is selected, the
 * booking date is the pace booking date range resolved against the maximum
 * confirmation date; otherwise the horizon is parsed from the selected pace
 * horizon filter and the booking date is recalculated from the stay dates.
 * @param {Object} params
 * @returns {{paceHorizonValue: (number|string|null), bookingDate: {startDate: Date, endDate: Date}}}
 */
const resolvePaceBooking = ({
  paceBookingWindow,
  paceBookingDate,
  visualFilters,
  paceHorizon,
  targetDate,
  lastBookingDate,
  isForecast,
  pms,
  maxForecastDate,
}) => {
  if (paceBookingWindow === 'BPW') {
    return {
      paceHorizonValue: 'All',
      bookingDate: resolveCustomBookingDate(paceBookingDate, lastBookingDate, isForecast),
    };
  }
  return {
    paceHorizonValue: getPaceHorizonDays(visualFilters, paceHorizon),
    bookingDate: calculateConformationDate(
      targetDate,
      visualFilters,
      paceHorizon,
      lastBookingDate,
      isForecast,
      pms,
      maxForecastDate
    ),
  };
};
/**
 * Builds the filter payload for the Pace Time tabular visual.
 *
 * @param {Object} params
 * @param {{startDate: string, endDate: string}} params.targetDate - Selected stay date range.
 * @param {boolean} params.alignmentToggle - Indicates whether alignment mode is enabled.
 * @param {boolean} params.symmetricComparisonToggle - Indicates whether symmetric comparison is enabled.
 * @param {string} params.dateComparison - Selected comparison period.
 * @param {string} params.currencyType - Currency type used in the visual.
 * @param {Object} params.hotels - Selected hotel or hotel group information.
 * @param {number|string} params.pms - PMS identifier.
 * @param {Object} params.visualFilters - Applied visual-level filters.
 * @param {string[]} params.chartBreakdown - Selected chart breakdown hierarchy.
 * @param {boolean} params.isDetailed - Indicates whether detailed breakdown mode is enabled.
 * @param {Array<{label: string}>} params.focusOn - Selected focus-on breakdown values.
 * @param {boolean} params.resetBookingDate - Indicates whether booking date alignment should be reset.
 * @param {string} params.granularity - Selected granularity level.
 * @param {number} params.partialHours - Partial hours value used for comparison calculations.
 * @param {string|Date} params.lastBookingDate - Last available booking date.
 * @param {number} params.paceHorizon - Pace horizon value.
 * @param {boolean} params.isForecast - Indicates whether forecast mode is enabled.
 * @param {string|Date} params.maxForecastDate - Maximum forecast date available.
 * @param {{startDate: string, endDate: string}} params.paceBookingDate - Selected pace booking date range.
 * @param {string} params.paceBookingWindow - Selected booking window type.
 *
 * @returns {Object} Cerulean filter object containing stay dates, booking dates,
 * comparison dates, hotel filters, PMS filters, breakdown filters, currency,
 * granularity, and other Pace Time tabular filter settings.
 */
export const buildPaceTimeTabularFilters = ({
  targetDate,
  alignmentToggle,
  symmetricComparisonToggle,
  dateComparison,
  currencyType,
  hotels,
  pms,
  visualFilters,
  chartBreakdown,
  isDetailed,
  focusOn,
  resetBookingDate,
  granularity,
  partialHours,
  lastBookingDate,
  paceHorizon,
  isForecast,
  maxForecastDate,
  paceBookingDate,
  paceBookingWindow,
}) => {
  const { paceHorizonValue, bookingDate } = resolvePaceBooking({
    paceBookingWindow,
    paceBookingDate,
    visualFilters,
    paceHorizon,
    targetDate,
    lastBookingDate,
    isForecast,
    pms,
    maxForecastDate,
  });
  //
  const { adjustedStayDate, finalBookingDate, finalCompareBookingDate } =
    resolvePaceComparisonDates({
      targetDate,
      bookingDate,
      granularity,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      resetBookingDate,
      partialHours,
      lastBookingDate,
    });
  //
  const focusOnLabelList = focusOn?.map((element) => element?.label);
  const hotelIds = !hotels?.hotels
    ? [hotels?.hotelCode]
    : hotels?.hotels?.map((hotel) => hotel?.hotelCode);
  //
  return {
    ...createCeruleanDateFilter(STAY_DATE_LOWER, targetDate.startDate),
    ...createCeruleanDateFilter(STAY_DATE_UPPER, targetDate.endDate),
    ...createCeruleanDateFilter(COMPARE_STAY_DATE_LOWER, adjustedStayDate.startDate),
    ...createCeruleanDateFilter(COMPARE_STAY_DATE_UPPER, adjustedStayDate.endDate),
    ...createCeruleanDateFilter(BOOKING_DATE_LOWER, finalBookingDate.startDate),
    ...createCeruleanDateFilter(BOOKING_DATE_UPPER, finalBookingDate.endDate),
    ...createCeruleanDateFilter(COMPARE_BOOKING_DATE_LOWER, finalCompareBookingDate.startDate),
    ...createCeruleanDateFilter(COMPARE_BOOKING_DATE_UPPER, finalCompareBookingDate.endDate),
    ...createCeruleanFilter(CURRENCY_FILTER, currencyType),
    ...createCeruleanFilter(HOTEL_FILTER, hotelIds),
    ...createCeruleanFilter(COMPARISON_WINDOW_FILTER, COMPARE_WINDOW_MAP[dateComparison]),
    ...createCeruleanFilter(HOTEL_COMP_FILTER, hotelIds),
    ...createCeruleanFilter(ALIGNMENT_FILTER, alignmentToggle),
    ...createCeruleanFilter(PMS_FILTER, pms?.toString()),
    ...createCeruleanBreakdownSchemaFilter({ visualFilters, chartBreakdown, isDetailed }),
    ...createCeruleanFilter(BREAKDOWN_LIST_FILTER, focusOnLabelList),
    ...createCeruleanFilter(GRANULARITY_FILTER, granularity),
    ...createCeruleanFilter(PACE_HORIZON_FILTER, paceHorizonValue),
    ...createCeruleanFilter(MAX_BOOKING_FILTER, lastBookingDate),
  };
};
/**
 * Builds the filters accepted by the combined pace time segment lag aligned measure.
 *
 * @param {Object} params
 * @param {Object} params.targetDate - Selected stay date range.
 * @param {boolean} params.alignmentToggle - Indicates whether alignment mode is enabled.
 * @param {boolean} params.symmetricComparisonToggle - Indicates whether symmetric comparison is enabled.
 * @param {string} params.dateComparison - Selected comparison type/date range.
 * @param {string} params.currencyType - Currency type used for displaying values.
 * @param {Object} params.hotels - Selected hotel or hotel group.
 * @param {boolean} params.pms - PMS sync toggle state.
 * @param {Object[]} params.visualFilters - Applied visual-level filters.
 * @param {string} params.chartBreakdown - Selected breakdown.
 * @param {boolean} params.isDetailed - Indicates whether the detailed breakdown is enabled.
 * @param {Object[]} params.focusOn - Selected focus on values.
 * @param {boolean} params.resetBookingDate - Indicates whether the booking date has been reset.
 * @param {string} params.granularity - Selected granularity level.
 * @param {number} params.partialHours - Partial hours value used in calculations.
 * @param {string|Date} params.lastBookingDate - Last booking date included in the data.
 * @param {number} params.paceHorizon - Number of days in the pace horizon.
 * @param {boolean} params.isForecast - Indicates whether forecast mode is enabled.
 * @param {string|Date} params.maxForecastDate - Maximum available forecast date.
 * @param {Object} params.paceBookingDate - Selected pace booking date.
 * @param {string} params.paceBookingWindow - Selected booking window value.
 * @returns {Object} Cerulean filters for the pace time segment lag aligned measure.
 */
export const buildPaceTimeSegmentTabularFilters = ({
  targetDate,
  alignmentToggle,
  symmetricComparisonToggle,
  dateComparison,
  currencyType,
  hotels,
  pms,
  visualFilters,
  chartBreakdown,
  isDetailed,
  focusOn,
  resetBookingDate,
  granularity,
  partialHours,
  lastBookingDate,
  paceHorizon,
  isForecast,
  maxForecastDate,
  paceBookingDate,
  paceBookingWindow,
}) => {
  const { paceHorizonValue, bookingDate } = resolvePaceBooking({
    paceBookingWindow,
    paceBookingDate,
    visualFilters,
    paceHorizon,
    targetDate,
    lastBookingDate,
    isForecast,
    pms,
    maxForecastDate,
  });
  //
  const { adjustedStayDate, finalBookingDate, finalCompareBookingDate } =
    resolvePaceComparisonDates({
      targetDate,
      bookingDate,
      granularity,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      resetBookingDate,
      partialHours,
      lastBookingDate,
    });
  //
  const focusOnLabelList = focusOn?.map((element) => element?.label);
  const hotelIds = !hotels?.hotels
    ? [hotels?.hotelCode]
    : hotels?.hotels?.map((hotel) => hotel?.hotelCode);
  //
  return {
    ...createCeruleanDateFilter(STAY_DATE_LOWER, targetDate.startDate),
    ...createCeruleanDateFilter(STAY_DATE_UPPER, targetDate.endDate),
    ...createCeruleanDateFilter(COMPARE_STAY_DATE_LOWER, adjustedStayDate.startDate),
    ...createCeruleanDateFilter(COMPARE_STAY_DATE_UPPER, adjustedStayDate.endDate),
    ...createCeruleanDateFilter(BOOKING_DATE_LOWER, finalBookingDate.startDate),
    ...createCeruleanDateFilter(BOOKING_DATE_UPPER, finalBookingDate.endDate),
    ...createCeruleanDateFilter(COMPARE_BOOKING_DATE_LOWER, finalCompareBookingDate.startDate),
    ...createCeruleanDateFilter(COMPARE_BOOKING_DATE_UPPER, finalCompareBookingDate.endDate),
    ...createCeruleanFilter(CURRENCY_FILTER, currencyType),
    ...createCeruleanFilter(HOTEL_FILTER, hotelIds),
    ...createCeruleanFilter(COMPARISON_WINDOW_FILTER, COMPARE_WINDOW_MAP[dateComparison]),
    ...createCeruleanFilter(ALIGNMENT_FILTER, alignmentToggle),
    ...createCeruleanFilter(PMS_FILTER, pms?.toString()),
    ...createCeruleanBreakdownSchemaFilter({ visualFilters, chartBreakdown, isDetailed }),
    ...createCeruleanFilter(BREAKDOWN_LIST_FILTER, focusOnLabelList),
    ...createCeruleanFilter(GRANULARITY_FILTER, granularity),
    ...createCeruleanFilter(PACE_HORIZON_FILTER, paceHorizonValue),
    // Required by the breakdown list getter measure, which shares these filters
    ...createCeruleanFilter(HOTEL_COMP_FILTER, hotelIds),
  };
};
/**
 * Builds the header data displayed in the Pace tabular visual.
 *
 * @param {Object} params
 * @param {string|Date} params.targetDate - Selected target/stay date.
 * @param {boolean} params.alignmentToggle - Indicates whether alignment mode is enabled.
 * @param {boolean} params.symmetricComparisonToggle - Indicates whether symmetric comparison is enabled.
 * @param {string} params.dateComparison - Selected comparison type/date range.
 * @param {string} params.currencyType - Currency type used for displaying values.
 * @param {string} params.hotelName - Name of the selected hotel.
 * @param {string} params.pms - Property Management System associated with the hotel.
 * @param {Object} params.visualFilters - Applied visual-level filters.
 * @param {boolean} params.resetBookingDate - Indicates whether the booking date has been reset.
 * @param {number} params.partialHours - Partial hours value used in calculations.
 * @param {string|Date} params.lastBookingDate - Last booking date included in the data.
 * @param {number} params.paceHorizon - Number of days in the pace horizon.
 * @param {boolean} params.isForecast - Indicates whether forecast mode is enabled.
 * @param {string|Date} params.maxForecastDate - Maximum available forecast date.
 * @param {string|Date} params.paceBookingDate - Selected pace booking date.
 * @param {number} params.paceBookingWindow - Selected booking window value.
 * @param {string} params.granularity - Selected granularity level.
 *
 * @returns {{
 *   title: string,
 *   hotelName: string,
 *   sections: Array<{
 *     title: string,
 *     value: string
 *   }>
 * }}
 * An object containing the header information for the Pace tabular visual,
 * including the title, hotel name, and a collection of labeled section values.
 */
export const buildPaceHeaderData = ({
  targetDate,
  alignmentToggle,
  symmetricComparisonToggle,
  dateComparison,
  currencyType,
  hotelName,
  pms,
  visualFilters,
  resetBookingDate,
  partialHours,
  lastBookingDate,
  paceHorizon,
  isForecast,
  maxForecastDate,
  paceBookingDate,
  paceBookingWindow,
  granularity,
}) => {
  const { paceHorizonValue, bookingDate } = resolvePaceBooking({
    paceBookingWindow,
    paceBookingDate,
    visualFilters,
    paceHorizon,
    targetDate,
    lastBookingDate,
    isForecast,
    pms,
    maxForecastDate,
  });
  //
  const { adjustedStayDate, finalBookingDate, finalCompareBookingDate } =
    resolvePaceComparisonDates({
      targetDate,
      bookingDate,
      granularity,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      resetBookingDate,
      partialHours,
      lastBookingDate,
    });
  //
  const stayDateRange = `${format(
    new Date(targetDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(targetDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;

  const stayDateComparison = `${format(
    new Date(adjustedStayDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(adjustedStayDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  //
  const bookingDateRange = `${format(
    new Date(finalBookingDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(finalBookingDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  const bookingDateComparison = `${format(
    new Date(finalCompareBookingDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(finalCompareBookingDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  const isCustomWindow = paceBookingWindow === PACE_HORIZON_SELECTION.BOOKING_PACE_WINDOW;
  const bookingDateValue = isCustomWindow
    ? bookingDateRange
    : getPaceHorizonBookingLabel(lastBookingDate);
  const bookingComparisonValue = isCustomWindow
    ? bookingDateComparison
    : getPaceHorizonComparisonLabel({
        targetDate,
        bookingDate,
        alignmentToggle,
        symmetricComparisonToggle,
        dateComparison,
        resetBookingDate,
        partialHours,
        lastBookingDate,
      }) ?? bookingDateComparison;
  //
  let comparisonLabel = 'Previous Year - Unaligned';
  if (alignmentToggle) {
    switch (dateComparison) {
      case compareWindows.py:
        comparisonLabel = 'Previous Year - Aligned';
        break;
      case compareWindows.pm:
        comparisonLabel = 'Previous Month - Aligned';
        break;
      case compareWindows.pw:
        comparisonLabel = 'Previous Week';
        break;
      default:
        comparisonLabel = 'Custom - Aligned';
        break;
    }
  } else {
    switch (dateComparison) {
      case compareWindows.py:
        comparisonLabel = 'Previous Year - Unaligned';
        break;
      case compareWindows.pm:
        comparisonLabel = 'Previous Month - Unaligned';
        break;
      case compareWindows.pw:
        comparisonLabel = 'Previous Week';
        break;
      default:
        comparisonLabel = 'Custom - Unaligned';
        break;
    }
  }
  //
  return {
    title: PACE_TIME_TABULAR_TITLE,
    hotelName,
    sections: [
      { title: STAY_DATE_RANGE, value: stayDateRange, id: 1 },
      { title: STAY_DATE_COMPARISON, value: stayDateComparison, id: 2 },
      { title: BOOKING_DATE_RANGE, value: bookingDateValue, id: 3 },
      {
        title: BOOKING_DATE_COMPARISON,
        value: bookingComparisonValue,
        id: 4,
      },
      { title: PACE_HORIZON, value: paceHorizonValue, id: 5 },
      { title: COMPARISON, value: comparisonLabel, id: 6 },
      { title: REPORTING_CURRENCY, value: currencyType, id: 7 },
    ],
  };
};
//
export const getPaceTimeTitle = (isCumulative, isLagAligned) => {
  if (isCumulative && isLagAligned) {
    return PACE_TIME_CUMULATIVE_LAG_ALIGNED__TITLE;
  }
  if (isCumulative) {
    return PACE_TIME_CUMULATIVE_TITLE;
  }
  if (isLagAligned) {
    return PACE_TIME_TABULAR_LAG_ALIGNED_TITLE;
  }
  return PACE_TIME_TABULAR_TITLE;
};
/**
 * Builds the filter payload for the Pace Hotel tabular visual.
 *
 * @param {Object} params
 * @param {{startDate: string, endDate: string}} params.targetDate - Selected stay date range.
 * @param {boolean} params.alignmentToggle - Indicates whether alignment mode is enabled.
 * @param {boolean} params.symmetricComparisonToggle - Indicates whether symmetric comparison is enabled.
 * @param {string} params.dateComparison - Selected comparison period.
 * @param {string} params.currencyType - Currency type used in the visual.
 * @param {Object} params.hotels - Selected hotel or hotel group information.
 * @param {number|string} params.pms - PMS identifier.
 * @param {Object} params.visualFilters - Applied visual-level filters.
 * @param {string[]} params.chartBreakdown - Selected chart breakdown hierarchy.
 * @param {boolean} params.isDetailed - Indicates whether detailed breakdown mode is enabled.
 * @param {Array<{label: string}>} params.focusOn - Selected focus-on breakdown values.
 * @param {boolean} params.resetBookingDate - Indicates whether booking date alignment should be reset.
 * @param {string} params.granularity - Selected granularity level.
 * @param {number} params.partialHours - Partial hours value used for comparison calculations.
 * @param {string|Date} params.lastBookingDate - Last available booking date.
 * @param {number} params.paceHorizon - Pace horizon value.
 * @param {boolean} params.isForecast - Indicates whether forecast mode is enabled.
 * @param {string|Date} params.maxForecastDate - Maximum forecast date available.
 * @param {{startDate: string, endDate: string}} params.paceBookingDate - Selected pace booking date range.
 * @param {string} params.paceBookingWindow - Selected booking window type.
 *
 * @returns {Object} Cerulean filter object containing stay dates, booking dates,
 * comparison dates, hotel filters, PMS filters, breakdown filters, currency,
 * granularity, and other Pace Time tabular filter settings.
 */
export const buildPaceHotelTabularFilters = ({
  targetDate,
  alignmentToggle,
  symmetricComparisonToggle,
  dateComparison,
  currencyType,
  hotels,
  hotelFocusOn,
  pms,
  visualFilters,
  chartBreakdown,
  isDetailed,
  focusOn,
  resetBookingDate,
  granularity,
  partialHours,
  lastBookingDate,
  paceHorizon,
  isForecast,
  maxForecastDate,
  paceBookingDate,
  paceBookingWindow,
  isNormalized,
}) => {
  const compHotelIds = !hotelFocusOn?.hotels
    ? [hotelFocusOn?.hotelCode]
    : hotelFocusOn?.hotels?.map((hotel) => hotel?.hotelCode);
  //
  const { paceHorizonValue, bookingDate } = resolvePaceBooking({
    paceBookingWindow,
    paceBookingDate,
    visualFilters,
    paceHorizon,
    targetDate,
    lastBookingDate,
    isForecast,
    pms,
    maxForecastDate,
  });
  //
  const { adjustedStayDate, finalBookingDate, finalCompareBookingDate } =
    resolvePaceComparisonDates({
      targetDate,
      bookingDate,
      granularity,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      resetBookingDate,
      partialHours,
      lastBookingDate,
    });
  //
  const focusOnLabelList = focusOn?.map((element) => element?.label);
  const hotelIds = !hotels?.hotels
    ? [hotels?.hotelCode]
    : hotels?.hotels?.map((hotel) => hotel?.hotelCode);
  //
  return {
    ...createCeruleanDateFilter(STAY_DATE_LOWER, targetDate.startDate),
    ...createCeruleanDateFilter(STAY_DATE_UPPER, targetDate.endDate),
    ...createCeruleanDateFilter(COMPARE_STAY_DATE_LOWER, adjustedStayDate.startDate),
    ...createCeruleanDateFilter(COMPARE_STAY_DATE_UPPER, adjustedStayDate.endDate),
    ...createCeruleanDateFilter(BOOKING_DATE_LOWER, finalBookingDate.startDate),
    ...createCeruleanDateFilter(BOOKING_DATE_UPPER, finalBookingDate.endDate),
    ...createCeruleanDateFilter(COMPARE_BOOKING_DATE_LOWER, finalCompareBookingDate.startDate),
    ...createCeruleanDateFilter(COMPARE_BOOKING_DATE_UPPER, finalCompareBookingDate.endDate),
    ...createCeruleanFilter(CURRENCY_FILTER, currencyType),
    ...createCeruleanFilter(HOTEL_FILTER, hotelIds),
    ...createCeruleanFilter(COMPARISON_WINDOW_FILTER, COMPARE_WINDOW_MAP[dateComparison]),
    ...createCeruleanFilter(HOTEL_COMP_FILTER, hotelIds),
    ...createCeruleanFilter(ALIGNMENT_FILTER, alignmentToggle),
    ...createCeruleanFilter(PMS_FILTER, pms?.toString()),
    ...createCeruleanBreakdownSchemaFilter({ visualFilters, chartBreakdown, isDetailed }),
    ...createCeruleanFilter(BREAKDOWN_LIST_FILTER, focusOnLabelList),
    ...createCeruleanFilter(GRANULARITY_FILTER, granularity),
    ...createCeruleanFilter(HOTEL_COMP_FILTER, compHotelIds),
    ...createCeruleanFilter(NORMALIZED_FILTER, isNormalized),
    ...createCeruleanFilter(PACE_HORIZON_FILTER, paceHorizonValue),
    ...createCeruleanFilter(MAX_BOOKING_FILTER, lastBookingDate),
  };
};
/**
 * Builds the header data displayed in the Pace Hotel tabular visual.
 *
 * @param {Object} params
 * @param {string|Date} params.targetDate - Selected target/stay date.
 * @param {string} params.currencyType - Currency type used for displaying values.
 * @param {number} params.paceHorizon - Number of days in the pace horizon.
 * @param {string|Date} params.paceBookingDate - Selected pace booking date.
 * @param {string} params.granularity - Selected granularity level.
 *
 * @returns {{
 *   title: string,
 *   hotelName: string,
 *   sections: Array<{
 *     title: string,
 *     value: string
 *   }>
 * }}
 * An object containing the header information for the Pace tabular visual,
 * including the title, hotel name, and a collection of labeled section values.
 */
export const buildPaceHotelHeaderData = ({
  targetDate,
  currencyType,
  hotelName,
  hotelFocusOn,
  pms,
  visualFilters,
  lastBookingDate,
  paceHorizon,
  isForecast,
  maxForecastDate,
  paceBookingDate,
  paceBookingWindow,
  granularity,
}) => {
  const { paceHorizonValue, bookingDate } = resolvePaceBooking({
    paceBookingWindow,
    paceBookingDate,
    visualFilters,
    paceHorizon,
    targetDate,
    lastBookingDate,
    isForecast,
    pms,
    maxForecastDate,
  });
  //
  const finalBookingDate = adjustDateRangeByGranularity(bookingDate, granularity);
  //
  const stayDateRange = `${format(
    new Date(targetDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(targetDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  //
  const bookingDateRange = `${format(
    new Date(finalBookingDate.startDate),
    YEAR_MONTH_DAY_PATTERN
  )} to ${format(new Date(finalBookingDate.endDate), YEAR_MONTH_DAY_PATTERN)}`;
  //
  return {
    title: PACE_TIME_TABULAR_TITLE,
    hotelName,
    comparisonHotelName: hotelFocusOn,
    sections: [
      { title: STAY_DATE_RANGE, value: stayDateRange, id: 1 },
      {
        title: BOOKING_DATE_RANGE,
        value:
          paceBookingWindow === PACE_HORIZON_SELECTION.BOOKING_PACE_WINDOW
            ? bookingDateRange
            : getPaceHorizonBookingLabel(lastBookingDate),
        id: 3,
      },

      { title: PACE_HORIZON, value: paceHorizonValue, id: 5 },
      { title: REPORTING_CURRENCY, value: currencyType, id: 7 },
    ],
  };
};
//
export const buildPaceSegmentSchema = (isLagAligned, data = {}) => [
  ...(isLagAligned
    ? [
        {
          id: 'Lag',
          label: 'Metric',
          children: [
            {
              id: 'Lag',
              label: 'lags',
              type: 'number',
              align: 'right',
              disableNegativeColor: true,
            },
          ],
        },
      ]
    : [
        {
          id: 'BookingDate',
          label: 'Date',
          children: [
            {
              id: 'BookingDate',
              label: 'BookingDate',
              type: 'string',
              align: 'left',
            },
          ],
        },
      ]),
  {
    id: 'room_nights',
    label: 'Room Nights',
    children: getColumnsBySuffix(data, '_room_nights'),
  },
  {
    id: 'revenue',
    label: 'Revenue',
    children: getColumnsBySuffix(data, '_revenue'),
  },
  {
    id: 'adr',
    label: 'ADR',
    children: getColumnsBySuffix(data, '_ADR'),
  },
  {
    id: 'alos',
    label: 'ALOS',
    children: getColumnsBySuffix(data, '_ALOS'),
  },
  {
    id: 'lead_time',
    label: 'Lead Time',
    children: getColumnsBySuffix(data, '_LeadTime').map((col) =>
      col.label === 'Total' ? { ...col, label: 'Overall' } : col
    ),
  },
  {
    id: 'cancellation',
    label: 'Cancellation Count',
    children: getColumnsBySuffix(data, '_cancellation'),
  },
];
/**
 * Groups flat pace rows into a parent row per key value with the breakdown rows as its children.
 *
 * Rows where the breakdown column equals 'Total' become the parent of the rows sharing the same
 * key value; each child then carries its breakdown name in the key column so the hierarchy reads
 * as `lag -> segment`. The grand total row is appended last for MuiTabular's aggregation row.
 *
 * @param {Array<Object>} data - Flat rows returned by the visual data endpoint.
 * @param {string} key - Breakdown column name, from getBreakdownName.
 * @param {string} [labelKey='Lag'] - Column holding the row label (the lag).
 * @returns {Array<Object>} Rows shaped for MuiTabular, parents carrying a `children` array.
 */
export const groupPaceDataWithParent = (data, key, labelKey = 'Lag') => {
  if (!Array.isArray(data)) return [];
  // Rows rolled up across every breakdown value become the parent rows
  const parents = data.filter((row) => row?.[key] === 'Total' && row?.[labelKey] !== 'Total');
  if (!parents?.length) {
    return [];
  }
  const children = data.filter((row) => row?.[key] !== 'Total');
  // Prefer the row totalled on both axes, falling back to a row totalled on the label column only
  const total =
    data.find((row) => row?.[labelKey] === 'Total' && row?.[key] === 'Total') ??
    data.find((row) => row?.[labelKey] === 'Total');
  //
  const result = parents.map((parent) => {
    const matchedChildren = children
      .filter((child) => child?.[labelKey] === parent?.[labelKey])
      .map((child) => {
        const newChild = { ...child, [labelKey]: child?.[key] };
        delete newChild[key];
        return newChild;
      });
    //
    const newParent = { ...parent };
    delete newParent[key];
    //
    return {
      ...newParent,
      children: matchedChildren,
    };
  });
  // Only append when the response actually carries a grand total row
  if (total) {
    const newTotal = { ...total };
    delete newTotal[key];
    result.push(newTotal);
  }
  return result;
};
//
export const getPaceSegmentTableTitle = (paceCumulative, paceLagAligned) => {
  if (paceCumulative && paceLagAligned) {
    return PACE_SEGMENT_CUMULATIVE_LAG_ALIGNED_TITLE;
  }
  if (paceCumulative && !paceLagAligned) {
    return PACE_SEGMENT_CUMULATIVE_TITLE;
  }
  if (!paceCumulative && paceLagAligned) {
    return PACE_SEGMENT_LAG_ALIGNED_TITLE;
  }
  return PACE_SEGMENT_TABULAR_TITLE;
};
